import {
  Divider,
  FormControlLabel,
  Switch,
  TextField
} from "@material-ui/core";
import DialogContentText from "@material-ui/core/DialogContentText";
import { hasPermission } from "@saleor/auth/misc";
import ActionDialog from "@saleor/components/ActionDialog";
import ActionDialogVariant from "@saleor/components/ActionDialogVariant";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import useBrandPrivateSearch from "@saleor/searches/useBrandPrivateSearch";
import useClusterSearch from "@saleor/searches/useClusterSearch";
import useMallQuery from "@saleor/searches/useMallQuery";
import {
  PermissionEnum,
  SmopOpStatusEnum,
  SmopRentalTypeEnum,
  SmopStatusEnum,
  SmopUserTypeCode,
  StoreTypeEnum,
  SubTenantTypeEnum
} from "@saleor/types/globalTypes";
import { localToUtc } from "@saleor/utils/formatters/convertTime";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import { canManageTenants } from "@saleor/utils/handlers/userHandler";
import Moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { findValueInEnum, maybe } from "../../misc";
import StoreEditPage, {
  StoreEditPageFormData
} from "../components/StoreEditPage/StoreEditPage";
import {
  DEFAULT_AUTO_CANCEL,
  DEFAULT_AUTO_CANCEL_DAYS,
  MAX_AUTO_CANCEL_DAYS,
  MIN_AUTO_CANCEL_DAYS
} from "../constants";
import {
  TenantDeleteMutation,
  TenantPromotionalRibbonMutation,
  TenantUpdateMutation,
  useTenantInStoreCancelConfigMutation
} from "../mutations";
import {
  usePaymentFeeRate,
  UseTenantDetailsQuery,
  useTenantInStoreCancelConfigQuery
} from "../queries";
import { TenantInStoreCancelConfigUpdate } from "../types/InStoreCancelConfig";
import { tenantDelete } from "../types/tenantDelete";
import { tenantPromotionalRibbonUpdate } from "../types/tenantPromotionalRibbonUpdate";
import { tenantUpdate } from "../types/tenantUpdate";
import { storeListPath, storeUrl } from "../urls";

interface StoreEditProps {
  id: string;
  params: any;
}

const StoreEdit: React.FC<StoreEditProps> = ({ id, params }) => {
  const { user } = useUser();
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();

  const hasManageTenantPermission = canManageTenants();

  const [isModal, setIsModal] = React.useState(false);

  const [
    isAutoCancelConfigModalOpen,
    setIsAutoCancelConfigModalOpen
  ] = useState(false);
  const [isAutoCancelSwitchEnabled, setIsAutoCancelSwitchEnabled] = useState(
    DEFAULT_AUTO_CANCEL
  );
  const [autoCancelDays, setAutoCancelDays] = useState(
    DEFAULT_AUTO_CANCEL_DAYS
  );
  const [autoCancelInputError, setAutoCancelInputError] = useState("");
  const [initialAutoCancelConfig, setInitialAutoCancelConfig] = useState(null);
  const [
    isNationwideDiscoverySwitchEnabled,
    setIsNationwideDiscoverySwitchEnabled
  ] = useState(false);

  const isSaveAutoCancelConfigDisabled = useMemo(() => {
    if (autoCancelInputError) {
      return true;
    }
    if (!autoCancelDays) {
      return true;
    }
    if (!initialAutoCancelConfig) {
      return false;
    }

    const noChanges =
      initialAutoCancelConfig.isEnabled === isAutoCancelSwitchEnabled &&
      String(initialAutoCancelConfig.cancellationDays) === autoCancelDays &&
      initialAutoCancelConfig.allowNationwideDiscovery ===
        isNationwideDiscoverySwitchEnabled;

    return noChanges;
  }, [
    initialAutoCancelConfig,
    isAutoCancelSwitchEnabled,
    autoCancelDays,
    autoCancelInputError,
    isNationwideDiscoverySwitchEnabled
  ]);

  const validateAutoCancelDays = (value: number) => {
    if (value < MIN_AUTO_CANCEL_DAYS || value > MAX_AUTO_CANCEL_DAYS) {
      setAutoCancelInputError(`Please enter a value of 1 to 365.`);
    } else {
      setAutoCancelInputError("");
    }
  };

  return (
    <UseTenantDetailsQuery displayLoader variables={{ id }}>
      {({ data, loading }) => {
        const storeDetails = data?.tenantPrivate;

        const {
          data: tenantInStoreCancelConfigData,
          refetch
        } = useTenantInStoreCancelConfigQuery({
          skip: loading,
          variables: { tenantId: id }
        });
        const config = tenantInStoreCancelConfigData?.tenantInStoreCancelConfig;

        useEffect(() => {
          if (config != null) {
            setIsAutoCancelSwitchEnabled(config.isEnabled);
            setAutoCancelDays(String(config.cancellationDays));
            setIsNationwideDiscoverySwitchEnabled(
              config.allowNationwideDiscovery
            );

            setInitialAutoCancelConfig({
              allowNationwideDiscovery: config.allowNationwideDiscovery,
              cancellationDays: config.cancellationDays,
              isEnabled: config.isEnabled
            });
          }
        }, [config]);

        const handleCloseAutoCancelConfigModal = () => {
          setIsAutoCancelConfigModalOpen(false);
          setIsAutoCancelSwitchEnabled(
            config?.isEnabled ?? DEFAULT_AUTO_CANCEL
          );
          setAutoCancelDays(
            config ? String(config?.cancellationDays) : DEFAULT_AUTO_CANCEL_DAYS
          );
          setAutoCancelInputError("");
          setIsNationwideDiscoverySwitchEnabled(
            config?.allowNationwideDiscovery ?? false
          );
        };

        const handleInStoreCancelConfigUpdateOnComplete = (
          data: TenantInStoreCancelConfigUpdate
        ) => {
          if (!data?.inStoreCancelConfigErrors?.length) {
            notify({
              status: "success",
              text: intl.formatMessage({
                defaultMessage: "In-store cancel config updated"
              })
            });
            refetch();
            setIsAutoCancelConfigModalOpen(false);
          } else {
            data.inStoreCancelConfigErrors.forEach(error => {
              notify({
                status: "error",
                text: error.message
              });
            });
          }
        };

        const handleInStoreCancelConfigUpdateOnConfirm = () => {
          if (
            !hasPermission(PermissionEnum.MANAGE_IN_STORE_CANCEL_CONFIG, user)
          ) {
            notify({
              status: "error",
              text: "You do not have permission to perform this action."
            });
            return;
          }

          updateConfig({
            variables: {
              input: {
                allowNationwideDiscovery: isNationwideDiscoverySwitchEnabled,
                cancellationDays: +autoCancelDays,
                isEnabled: isAutoCancelSwitchEnabled,
                tenantId: id
              }
            }
          });
        };

        const [updateConfig] = useTenantInStoreCancelConfigMutation({
          onCompleted: handleInStoreCancelConfigUpdateOnComplete
        });

        const handleStoreEdit = (data: tenantUpdate) => {
          if (!maybe(() => data.tenantUpdate.errors.length !== 0)) {
            notify({
              status: "success",
              text: intl.formatMessage({ defaultMessage: "Store updated" })
            });
            navigate(storeUrl(data.tenantUpdate.tenant.id));
          } else if (
            data.tenantUpdate.errors.some(e => e.code === "REQUIRED")
          ) {
            notify({
              status: "error",
              text: intl.formatMessage({
                defaultMessage: "Some Fields have null value!"
              })
            });
          } else if (
            data.tenantUpdate.errors.some(e => e.code === "INVALID") &&
            data.tenantUpdate.errors.some(e => e.field === "status")
          ) {
            notify({
              status: "error",
              text: data.tenantUpdate.errors[0].message
            });
          } else {
            notify({
              status: "error",
              text: intl.formatMessage({ defaultMessage: "Invalid Form!" })
            });
          }
        };
        const [msg, setMessage] = useState("");
        useEffect(() => {
          if (msg) {
            notify({
              status: "error",
              text: msg
            });
          }
        }, [msg]);
        const handleStoreEditPromotionalRibbon = (
          data: tenantPromotionalRibbonUpdate
        ) => {
          setMessage("");
          if (
            !maybe(() => data.tenantPromotionalRibbonUpdate.errors.length !== 0)
          ) {
            notify({
              status: "success",
              text: intl.formatMessage({ defaultMessage: "Store updated" })
            });
            navigate("/store");
          } else if (
            data.tenantPromotionalRibbonUpdate.errors.some(
              e => e.code === "REQUIRED"
            )
          ) {
            notify({
              status: "error",
              text: intl.formatMessage({
                defaultMessage: "Some Fields have null value!"
              })
            });
          } else if (
            data.tenantPromotionalRibbonUpdate.errors.some(
              e => e.code === "INVALID"
            ) &&
            data.tenantPromotionalRibbonUpdate.errors.some(
              e => e.field === "promoRibbonStart"
            )
          ) {
            setMessage(data.tenantPromotionalRibbonUpdate.errors[0].message);
            // notify({
            //   status: "error",
            //   text: intl.formatMessage(commonMessages.timeError)
            // });
          } else {
            notify({
              status: "error",
              text: intl.formatMessage({ defaultMessage: "Invalid Form!" })
            });
          }
        };

        const handleDeleteSuccess = (data: tenantDelete) => {
          if (data?.updateTenantStatus?.errors.length === 0) {
            notify({
              status: "success",
              text: intl.formatMessage({
                defaultMessage: "Store deleted"
              })
            });
            navigate(storeListPath);
          } else if (
            data?.updateTenantStatus?.errors.some(e => e.field === "status") &&
            data?.updateTenantStatus?.errors.some(e => e.code === "INVALID")
          ) {
            notify({
              status: "error",
              text: intl.formatMessage({
                defaultMessage: "Can't delete a store with published products"
              })
            });

            closeModal();
          } else {
            notify({
              status: "error",
              text: intl.formatMessage({
                defaultMessage: "Something went wrong. Please Try again!"
              })
            });
          }
        };

        const { data: paymentFeeRateChoices } = usePaymentFeeRate({
          displayLoader: true
        });

        const brandInitialSearchData = {
          after: null,
          first: 20,
          query: "",
          status: SmopStatusEnum.ACT
        };
        // AUTOCOMPLETE SEARCH
        const {
          loadMore: loadMoreBrand,
          search: searchBrands,
          result: brandsQueryData
        } = useBrandPrivateSearch({
          variables: {
            ...brandInitialSearchData,
            accountManager:
              user.userType === SmopUserTypeCode.SELLER_MASTER ? user.id : null
          }
        });
        const {
          loadMore: loadMoreMall,
          search: searchMalls,
          result: mallsQueryData
        } = useMallQuery({
          variables: {
            after: null,
            first: 20,
            query: "",
            statuses: [SmopStatusEnum.ACT]
          }
        });

        const searchCluster = useClusterSearch({
          variables: {
            ...DEFAULT_INITIAL_SEARCH_DATA,
            first: 5
          }
        });
        const clusterChoices = maybe(
          () => searchCluster?.result?.data?.search?.edges,
          []
        ).map(edge => edge.node);
        const sellerBrandChoices = maybe(
          () => brandsQueryData?.data?.search?.edges,
          []
        ).map(edge => edge.node);
        const mallChoices = maybe(
          () => mallsQueryData?.data?.search?.edges,
          []
        ).map(edge => edge.node);

        const [deleteStore, deleteStoreResult] = TenantDeleteMutation({
          onCompleted: handleDeleteSuccess
        });

        const [openModal, closeModal] = createDialogActionHandlers(
          navigate,
          params => storeUrl(id, params),
          params
        );

        const handleDeleteStore = id => {
          deleteStore({
            variables: {
              id,
              status: SmopStatusEnum.DEL
            }
          });
        };
        return (
          <TenantUpdateMutation onCompleted={handleStoreEdit}>
            {(tenantUpdate, tenantUpdateResult) => (
              <TenantPromotionalRibbonMutation
                onCompleted={handleStoreEditPromotionalRibbon}
              >
                {(
                  tenantPromotionalRibbonUpdate,
                  tenantPromotionalRibbonUpdateResult
                ) => {
                  const handleSubmit = async (
                    data: StoreEditPageFormData,
                    file
                  ) => {
                    const handleIsOtherBusiness = value => {
                      if (value === "true" || value === true) {
                        return true;
                      } else if (value === "false" || value === false) {
                        return false;
                      }
                    };
                    const newStartTime = Moment(
                      "12/10/2021 " + data.operationStartTime
                    ).format("DD/MM/YYYY HH:mm");
                    const newEndTime = Moment(
                      "12/10/2021 " + data.operationEndTime
                    ).format("DD/MM/YYYY HH:mm");
                    if (
                      user.userType.code === SmopUserTypeCode.SELLER_MASTER ||
                      user.userType.code === SmopUserTypeCode.STORE_ADMIN
                    ) {
                      const inputData = {
                        isExpressLongDistanceDeliveryAllowed: handleIsOtherBusiness(
                          data.isExpressLongDistanceDeliveryAllowed
                        ),
                        ...(data.storeType === "GENERAL_MERCHANDISE" && {
                          isInStorePickupEnabled: data.isInStorePickup
                        }),
                        isSameDayDeliveryAllowed: handleIsOtherBusiness(
                          data.isSameDayDeliveryAllowed
                        ),
                        ...(data.storeType === "GENERAL_MERCHANDISE" && {
                          isStandardDeliveryEnabled:
                            data.isStandardDeliveryEnabled
                        }),
                        location: data.location,
                        operationEndTime: Moment(newEndTime).format("HH:mm:ss"),
                        operationStartTime: Moment(newStartTime).format(
                          "HH:mm:ss"
                        ),
                        promoRibbonDescription: data.promoRibbonDescription,
                        promoRibbonEnd:
                          data.promoRibbonEnd !== "Invalid date"
                            ? localToUtc(data.promoRibbonEnd)
                            : null,
                        promoRibbonStart:
                          data.promoRibbonStart !== "Invalid date"
                            ? localToUtc(data.promoRibbonStart)
                            : null,
                        status: data.isActivate
                      };
                      const result = await tenantPromotionalRibbonUpdate({
                        variables: {
                          id,
                          input: inputData
                        }
                      });
                      return result.data.tenantPromotionalRibbonUpdate.errors;
                    } else {
                      const inputData = {
                        bankAccountName: data.acctName,
                        bankAccountNo: data.acctNum,
                        bankName: data.bankName,
                        ...(file && { bannerImage: file }),
                        ...(data.bannerImage === null && { bannerImage: file }),
                        brand: data.sellerBrand,
                        cluster: data.cluster,
                        contractId: data.contractId,
                        financeEmail: data.financeEmailAddress,
                        financeMobileNo: data.financeMobileNum,
                        financeName: data.financeName,
                        ipay88SubMid: data.ipay88SubMid,
                        isExpressLongDistanceDeliveryAllowed: handleIsOtherBusiness(
                          data.isExpressLongDistanceDeliveryAllowed
                        ),
                        isFeatured: data.isFeatured,
                        ...(data.storeType === "GENERAL_MERCHANDISE" && {
                          isInStorePickupEnabled: data.isInStorePickup
                        }),
                        isOtherBusiness: handleIsOtherBusiness(
                          data.isActivateCompanyDetails
                        ),
                        isSameDayDeliveryAllowed: data.isSameDayDeliveryAllowed,
                        ...(data.storeType === "GENERAL_MERCHANDISE" && {
                          isStandardDeliveryEnabled:
                            data.isStandardDeliveryEnabled
                        }),
                        isUseTenantCluster: data.isUseTenantCluster,
                        location: data.location,
                        mall: data.mallBranch,
                        operationEndTime: Moment(newEndTime).format("HH:mm:ss"),
                        operationStartTime: Moment(newStartTime).format(
                          "HH:mm:ss"
                        ),
                        operationStatus: SmopOpStatusEnum.OPN,
                        paymentFeeRate: data.paymentFeeRate,
                        rentalType: maybe(() =>
                          findValueInEnum(data.rentalType, SmopRentalTypeEnum)
                        ),
                        signatoryDesignation: data.designation,
                        signatoryEmail: data.authorizeEmailAddress,
                        signatoryMobileNo: data.authorizeMobileNum,
                        signatoryName: data.signatoryName,
                        status: data.isActivate,
                        subTenantType: maybe(() => {
                          const isFnB = data.storeType === "FOOD_AND_BEVERAGES";
                          if (!isFnB) {
                            return null;
                          }
                          if (!data.subTenantType) {
                            return null;
                          }
                          return (
                            findValueInEnum(
                              data.subTenantType
                                .replace(/ |-/g, "_")
                                .toLocaleUpperCase(),
                              SubTenantTypeEnum
                            ) || null
                          );
                        }),
                        tenantType:
                          maybe(() =>
                            findValueInEnum(data.storeType, StoreTypeEnum)
                          ) || null,
                        tinNo: data.tinNo
                      };
                      const result = await tenantUpdate({
                        variables: {
                          id,
                          input: inputData
                        }
                      });
                      return result.data.tenantUpdate.errors;
                    }
                  };

                  return (
                    <>
                      <StoreEditPage
                        onSubmit={handleSubmit}
                        disable={loading}
                        saveButtonBarState={
                          tenantUpdateResult.status ||
                          tenantPromotionalRibbonUpdateResult.status
                        }
                        storeData={maybe(() => storeDetails)}
                        brands={sellerBrandChoices}
                        malls={mallChoices}
                        paymentFeeRateChoices={paymentFeeRateChoices}
                        onDelete={() => openModal("delete")}
                        discardButton={setIsModal}
                        fetchMalls={query => searchMalls(query)}
                        fetchBrands={query => searchBrands(query)}
                        fetchMoreMalls={{
                          hasMore: maybe(
                            () =>
                              mallsQueryData.data.search.pageInfo.hasNextPage
                          ),
                          loading: mallsQueryData.loading,
                          onFetchMore: loadMoreMall
                        }}
                        fetchMoreBrands={{
                          hasMore: maybe(
                            () =>
                              brandsQueryData.data.search.pageInfo.hasNextPage
                          ),
                          loading: brandsQueryData.loading,
                          onFetchMore: loadMoreBrand
                        }}
                        clusterList={clusterChoices}
                        fetchClusters={query => searchCluster.search(query)}
                        fetchMoreClusters={{
                          hasMore: maybe(
                            () =>
                              searchCluster.result.data.search.pageInfo
                                .hasNextPage
                          ),
                          loading: searchCluster.result.loading,
                          onFetchMore: searchCluster.loadMore
                        }}
                        hasManageTenantPermission={hasManageTenantPermission}
                        setIsAutoCancelConfigModalOpen={
                          setIsAutoCancelConfigModalOpen
                        }
                        autoCancelConfig={config}
                      />
                      <ActionDialog
                        confirmButtonState={"default"}
                        onClose={() => {
                          setIsModal(false);
                        }}
                        onConfirm={() => navigate(storeListPath)}
                        open={isModal}
                        title={intl.formatMessage({
                          defaultMessage: "Discard changes",
                          description: "dialog title"
                        })}
                        variant="default"
                      >
                        <DialogContentText>
                          <FormattedMessage
                            defaultMessage={
                              "Changes will not be saved. Do you want to proceed?"
                            }
                          />
                        </DialogContentText>
                      </ActionDialog>
                      <ActionDialog
                        confirmButtonState={deleteStoreResult.status}
                        onClose={closeModal}
                        onConfirm={() => handleDeleteStore(id)}
                        open={params.action === "delete"}
                        title={intl.formatMessage({
                          defaultMessage: "Delete Store",
                          description: "dialog title"
                        })}
                        variant="delete"
                      >
                        <DialogContentText>
                          <FormattedMessage
                            defaultMessage={
                              "Are you sure you want to delete {storeName}?"
                            }
                            values={{
                              storeName: (
                                <strong>
                                  {maybe(() => storeDetails.brand.name, "...")}
                                </strong>
                              )
                            }}
                          />
                        </DialogContentText>
                      </ActionDialog>
                      <ActionDialogVariant
                        confirmButtonState="default"
                        onClose={handleCloseAutoCancelConfigModal}
                        onConfirm={handleInStoreCancelConfigUpdateOnConfirm}
                        defaultTitle={intl.formatMessage({
                          defaultMessage: "Manage In-Store Pickup",
                          description: "dialog title"
                        })}
                        open={isAutoCancelConfigModalOpen}
                        confirmButtonLabel="Save"
                        titleAlignment="start"
                        disabled={isSaveAutoCancelConfigDisabled}
                        dialogWidth="40em"
                      >
                        <div style={{ padding: "0 10px" }}>
                          <FormControlLabel
                            control={
                              <Switch
                                id="autoCancelToggle"
                                color="primary"
                                name="isAutoCancelEnabled"
                                checked={isAutoCancelSwitchEnabled}
                                onChange={e => {
                                  setIsAutoCancelSwitchEnabled(
                                    e.target.checked
                                  );
                                }}
                              />
                            }
                            label="Allow auto-cancellation for in-store pickup orders."
                            labelPlacement="end"
                            style={{ width: "100%" }}
                          />
                          <div style={{ marginLeft: 64, marginRight: 290 }}>
                            <TextField
                              fullWidth
                              id="autoCancelDays"
                              name="autoCancelDays"
                              label={intl.formatMessage({
                                defaultMessage: "No. of Days"
                              })}
                              onChange={e => {
                                const value = e.target.value;
                                setAutoCancelDays(value);
                                validateAutoCancelDays(+value);
                              }}
                              value={autoCancelDays}
                              onKeyPress={event => {
                                if (!/[0-9]/.test(event.key)) {
                                  event.preventDefault();
                                }
                              }}
                              InputProps={{
                                inputProps: {
                                  autoComplete: "off",
                                  max: 365,
                                  min: 1
                                }
                              }}
                              style={{ minWidth: 100, textAlign: "left" }}
                              error={!!autoCancelInputError}
                              helperText={autoCancelInputError}
                              disabled={!isAutoCancelSwitchEnabled}
                            />
                          </div>
                          <div
                            style={{
                              backgroundColor: "#FBF7B5",
                              borderLeft: "4px solid #E9B445",
                              borderRadius: "6px",
                              display: "flex",
                              fontSize: "13px",
                              margin: "12px 0 20px 64px",
                              padding: "0 16px",
                              textAlign: "left"
                            }}
                          >
                            <p>
                              In-store pickup orders tagged as "NEW" or
                              "Acknowledge" for more than the set number of days
                              will be automatically cancelled.
                            </p>
                          </div>
                          <Divider />
                          <FormControlLabel
                            control={
                              <Switch
                                id="nationwideDiscoveryToggle"
                                color="primary"
                                name="isNationwideDiscoverySwitchEnabled"
                                checked={isNationwideDiscoverySwitchEnabled}
                                onChange={e => {
                                  setIsNationwideDiscoverySwitchEnabled(
                                    e.target.checked
                                  );
                                }}
                              />
                            }
                            label={
                              <div
                                style={{ paddingTop: 20, textAlign: "start" }}
                              >
                                <div>Allow Nationwide Discovery</div>
                                <div
                                  style={{
                                    color: "gray",
                                    fontSize: "13px",
                                    marginTop: 6
                                  }}
                                >
                                  &#9432;&nbsp; Enable this option to offer this
                                  store's items nationwide.
                                </div>
                              </div>
                            }
                            labelPlacement="end"
                            style={{ width: "100%" }}
                          />
                        </div>
                      </ActionDialogVariant>
                    </>
                  );
                }}
              </TenantPromotionalRibbonMutation>
            )}
          </TenantUpdateMutation>
        );
      }}
    </UseTenantDetailsQuery>
  );
};

export default StoreEdit;
